/**
 * HYPSEYA — Clinical Progress Report
 * Monthly formal progress report. Exportable to PDF/print.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Chart, Cycle, ArrowUp, ArrowDown, Stable } from '@/components/HypIcons';
import orbUrl from '../orbAsset';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const wordmarkUrl = '/hypseya-wordmark.png';

const DEMO_REPORT = {
  ready: true,
  generated_at: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
  stats: {
    total_sessions: 24,
    eri_current: 71,
    eri_start: 48,
    eri_delta: 23,
    avg_regulation_delta: 8.4,
    avg_mood: 6.8,
  },
  highlights: [
    'ERI increased 23 points over the period — a clinically meaningful shift in regulation capacity.',
    'Client maintained practice consistency across 24 sessions, including two complete Daily Loop sequences.',
    'PHQ-9 scores declined from 14 (moderate) to 5 (minimal) — sustained across three assessment points.',
    'GAD-7 scores declined from 11 (moderate) to 4 (minimal) over the same period.',
    'Notable regulation improvement on historically difficult days (Monday, Friday).',
  ],
  narrative: {
    opening: 'This report covers 30 days of active emotional regulation practice using the HYPSEYA platform. The data reflects measurable progress across all three ERI dimensions.',
    regulation: 'The client demonstrated consistent improvement in moment-to-moment regulation capacity. ERI regulation sub-score moved from 44 to 68, with the most significant gains observed in weeks 3–4.',
    stability: 'Emotional stability indicators show reduced amplitude of daily fluctuation. The client reports fewer dysregulation episodes and improved return-to-baseline speed.',
    consistency: 'Practice frequency was above average (24 sessions / 30 days). No gaps longer than 2 days were recorded. This level of consistency is associated with durable skill consolidation.',
    integration: 'Voice-guided sessions were used 14 times, suggesting growing self-regulation autonomy. Client initiated Moment of Need sessions on 3 occasions, indicating real-world application.',
    recommendation: 'Continue current practice frequency. Consider introducing advanced breath-hold protocols and somatic anchoring exercises. A reassessment in 30 days is recommended to track continued PHQ-9 and GAD-7 trends.',
  },
};

function StatBlock({ label, value, unit, delta, higherBetter = true }) {
  const improved = delta > 0 === higherBetter || (delta < 0 && !higherBetter);
  const trendColor = delta === 0 || delta === null ? '#B8D3CF'
    : improved ? '#2D8B87' : '#B8906A';
  return (
    <div style={{
      flex: 1, minWidth: 90, background: 'rgba(247,243,236,0.8)',
      borderRadius: '12px', padding: '14px 12px', textAlign: 'center'
    }}>
      <div style={{ fontSize: '0.65rem', color: '#AAB8B6', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
        {value ?? '—'}{unit && <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#8FA3A0' }}>{unit}</span>}
      </div>
      {delta !== null && delta !== undefined && (
        <div style={{ fontSize: '0.72rem', color: trendColor, fontWeight: '600', marginTop: '2px' }}>
          {delta > 0 ? `+${delta}` : delta}
        </div>
      )}
    </div>
  );
}

function NarrativeSection({ title, text }) {
  if (!text) return null;
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{
        fontSize: '0.72rem', color: '#2D8B87', letterSpacing: '0.1em',
        textTransform: 'uppercase', fontWeight: '700', marginBottom: '6px'
      }}>
        {title}
      </div>
      <p style={{ margin: 0, fontSize: '0.9rem', color: '#444', lineHeight: '1.7' }}>
        {text}
      </p>
    </div>
  );
}

export default function ClinicalReport() {
  const navigate    = useNavigate();
  const printRef    = useRef(null);
  const [user, setUser]               = useState(null);
  const [report, setReport]           = useState(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [period, setPeriod]           = useState(30);
  const [isVisible, setIsVisible]     = useState(false);
  const [isRefreshing, setRefreshing] = useState(false);
  const [showShare, setShowShare]     = useState(false);
  const [shareLink, setShareLink]     = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied]           = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 80);
  }, []);

  useEffect(() => {
    if (!user?.user_id) {
      // No authenticated user — show demo
      setReport(DEMO_REPORT);
      setIsLoading(false);
      return;
    }
    load();
  }, [user, period]);

  const load = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/intelligence/clinical-report/${user.user_id}?period_days=${period}`);
      setReport(res.data);
    } catch {
      // Offline / backend unavailable — show demo report
      setReport(DEMO_REPORT);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const handlePrint = () => window.print();

  const handleGenerateLink = async () => {
    if (!user?.user_id) return;
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/share/report/${user.user_id}?period_days=${period}`);
      const base = window.location.origin;
      setShareLink(`${base}/therapist-report/${res.data.token}`);
    } catch {
      setShareLink('');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`HYPSEYA Clinical Report — ${user?.name || 'My client'}`);
    const body = encodeURIComponent(`Hi,\n\nI'd like to share my HYPSEYA Clinical Report with you.\n\n${shareLink || '[Generate a link first using the HYPSEYA app]'}\n\nThe report covers the last ${period} days of my emotional regulation practice.\n\nBest,\n${user?.name || ''}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const s = report?.stats;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
      padding: '28px 20px 100px', maxWidth: '680px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease'
    }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <button onClick={() => navigate(-1)} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#8FA3A0', fontSize: '0.82rem', padding: 0
        }}>
          <ArrowLeft size={14} color="#8FA3A0" /> Back
        </button>
        {report?.ready && (
          <button onClick={() => setShowShare(true)} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: '#1E6B68', border: 'none',
            borderRadius: '20px', padding: '9px 18px', cursor: 'pointer',
            color: '#fff', fontSize: '0.78rem', fontWeight: '600',
            boxShadow: '0 2px 10px rgba(30,107,104,0.25)'
          }}>
            ↗ Share with Therapist
          </button>
        )}

        {/* Share Modal */}
        {showShare && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }} onClick={() => setShowShare(false)}>
            <div style={{
              background: '#fff', borderRadius: '24px 24px 0 0',
              padding: '28px 24px 40px', width: '100%', maxWidth: '560px',
              boxShadow: '0 -8px 40px rgba(30,107,104,0.15)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ width: 36, height: 4, background: 'rgba(184,211,207,0.5)', borderRadius: 4, margin: '0 auto 24px' }} />
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.2rem', color: '#1E6B68', margin: '0 0 6px' }}>
                Share with your therapist
              </p>
              <p style={{ fontSize: '0.82rem', color: '#9AADAB', margin: '0 0 24px', lineHeight: '1.6' }}>
                Choose how to send your clinical report.
              </p>

              {/* Option 1 — Secure Link */}
              <div style={{ background: 'rgba(30,107,104,0.04)', borderRadius: '16px', padding: '18px 20px', marginBottom: '12px', border: '1px solid rgba(30,107,104,0.12)' }}>
                <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#1E6B68', fontSize: '0.9rem' }}>🔗 Secure link · valid 7 days</p>
                <p style={{ margin: '0 0 14px', fontSize: '0.78rem', color: '#9AADAB' }}>Your therapist opens the link — no account needed.</p>
                {shareLink ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input readOnly value={shareLink} style={{
                      flex: 1, padding: '10px 12px', borderRadius: '10px',
                      border: '1px solid rgba(184,211,207,0.5)', fontSize: '0.75rem',
                      color: '#555', background: '#fff', outline: 'none'
                    }} />
                    <button onClick={handleCopy} style={{
                      padding: '10px 16px', borderRadius: '10px', border: 'none',
                      background: copied ? '#2D8B87' : '#1E6B68', color: '#fff',
                      fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                    }}>
                      {copied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                ) : (
                  <button onClick={handleGenerateLink} disabled={shareLoading} style={{
                    width: '100%', padding: '11px', borderRadius: '10px', border: 'none',
                    background: shareLoading ? '#9AADAB' : '#1E6B68', color: '#fff',
                    fontWeight: '600', fontSize: '0.85rem', cursor: shareLoading ? 'default' : 'pointer'
                  }}>
                    {shareLoading ? 'Generating…' : 'Generate secure link'}
                  </button>
                )}
              </div>

              {/* Option 2 — Email */}
              <button onClick={handleEmail} style={{
                width: '100%', padding: '16px', borderRadius: '14px',
                border: '1px solid rgba(184,211,207,0.5)', background: '#fff',
                color: '#1E6B68', fontWeight: '600', fontSize: '0.85rem',
                cursor: 'pointer', marginBottom: '10px', textAlign: 'left'
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="11" rx="2" stroke="#1E6B68" strokeWidth="1.4"/><path d="M2 7l8 5 8-5" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Email your therapist
                </span>
              </button>

              {/* Option 3 — Browser Print */}
              <button onClick={handlePrint} style={{
                width: '100%', padding: '16px', borderRadius: '14px',
                border: '1px solid rgba(184,211,207,0.5)', background: '#fff',
                color: '#1E6B68', fontWeight: '600', fontSize: '0.85rem',
                cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10
              }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><rect x="3" y="7" width="14" height="9" rx="2" stroke="#1E6B68" strokeWidth="1.4"/><path d="M6 7V4h8v3" stroke="#1E6B68" strokeWidth="1.4" strokeLinejoin="round"/><rect x="6" y="12" width="8" height="1.5" rx="0.75" fill="#1E6B68"/></svg>
                Print / Save as PDF
              </button>
              {/* Option 4 — Server-side clinical PDF */}
              {user?.user_id && (
                <button onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/reports/clinical-pdf/${user.user_id}`, { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `hypseya_clinical_report_${new Date().toISOString().slice(0,10)}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { import('sonner').then(m => m.toast.error('PDF generation failed')); }
                }} style={{
                  width: '100%', padding: '16px', borderRadius: '14px',
                  border: '1.5px solid rgba(30,107,104,0.3)', background: 'rgba(30,107,104,0.05)',
                  color: '#1E6B68', fontWeight: '600', fontSize: '0.85rem',
                  cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10
                }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 3v9M7 9l3 3 3-3" stroke="#1E6B68" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 15h12" stroke="#1E6B68" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  Download clinical PDF
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Page title — always visible, so the empty state stays oriented too */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontSize: '1.7rem',
          color: '#1E3A39', margin: '0 0 4px', fontWeight: '500'
        }}>Clinical Report</h1>
        <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.82rem', lineHeight: 1.5 }}>
          A clinician-ready summary of your regulation practice — ready to share.
        </p>
      </div>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[30, 60, 90].map(d => (
          <button key={d} onClick={() => setPeriod(d)} style={{
            padding: '8px 18px',
            background: period === d ? '#1E6B68' : 'rgba(255,255,255,0.8)',
            color: period === d ? '#fff' : '#8FA3A0',
            border: `1px solid ${period === d ? '#1E6B68' : 'rgba(184,211,207,0.5)'}`,
            borderRadius: '20px', cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s'
          }}>
            {d} days
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={{ background: '#fff', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 20px rgba(30,107,104,0.07)' }}>
          <div style={{
            width: 40, height: 40, margin: '0 auto 16px',
            border: '2px solid rgba(184,211,207,0.3)', borderTop: '2px solid #2D8B87',
            borderRadius: '50%', animation: 'spin 1.2s linear infinite'
          }} />
          <p style={{ color: '#8FA3A0', fontSize: '0.88rem', fontStyle: 'italic' }}>Generating report…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : !report?.ready ? (
        <div style={{ background: '#fff', borderRadius: '20px', padding: '36px 28px', boxShadow: '0 2px 20px rgba(30,107,104,0.07)', textAlign: 'center' }}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <Chart size={40} color="#B8D3CF" />
          </div>
          <p style={{ color: '#8FA3A0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            {report?.message}
          </p>
        </div>
      ) : (
        <div ref={printRef} id="clinical-report-print">

          {/* Report header — print-styled */}
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '28px',
            marginBottom: '16px', boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={orbUrl} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                <img src={wordmarkUrl} alt="HYPSEYA" style={{ height: 14, opacity: 0.7 }} />
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.68rem', color: '#AAB8B6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Progress Report</div>
                <div style={{ fontSize: '0.78rem', color: '#8FA3A0' }}>{period}-day period · {report.generated_at}</div>
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <StatBlock label="Sessions"    value={s?.total_sessions}       unit="" delta={null} />
              <StatBlock label="ERI"         value={s?.eri_current}          unit="/100" delta={s?.eri_delta} higherBetter={true} />
              <StatBlock label="Avg Δ Reg."  value={s?.avg_regulation_delta} unit=" pts" delta={null} higherBetter={true} />
              <StatBlock label="Avg Mood"    value={s?.avg_mood}             unit="/10" delta={null} />
            </div>

            {s?.eri_delta !== null && s?.eri_delta !== undefined && (
              <div style={{
                padding: '10px 14px', borderRadius: '10px',
                background: s.eri_delta >= 0 ? 'rgba(45,139,135,0.07)' : 'rgba(184,144,106,0.07)',
                border: `1px solid ${s.eri_delta >= 0 ? 'rgba(45,139,135,0.2)' : 'rgba(184,144,106,0.2)'}`,
                fontSize: '0.82rem', color: s.eri_delta >= 0 ? '#2D8B87' : '#B8906A', fontWeight: '500'
              }}>
                ERI moved {s.eri_delta > 0 ? `+${s.eri_delta}` : s.eri_delta} points over this period
                {s.eri_start ? ` (${s.eri_start} → ${s.eri_current})` : ''}
              </div>
            )}
          </div>

          {/* Highlights */}
          {report.highlights?.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(30,107,104,0.05) 0%, rgba(184,211,207,0.1) 100%)',
              borderRadius: '16px', padding: '20px 24px', marginBottom: '16px',
              border: '1px solid rgba(184,211,207,0.4)'
            }}>
              <div style={{ fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '12px' }}>
                Key observations
              </div>
              {report.highlights.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2D8B87', marginTop: 7, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.88rem', color: '#444', lineHeight: '1.5' }}>{h}</span>
                </div>
              ))}
            </div>
          )}

          {/* Narrative */}
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', marginBottom: '16px', boxShadow: '0 2px 20px rgba(30,107,104,0.07)' }}>
            <div style={{ fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '20px' }}>
              Clinical narrative
            </div>
            {report.narrative && (
              <>
                {report.narrative.opening && (
                  <p style={{ margin: '0 0 20px', fontSize: '0.95rem', color: '#333', lineHeight: '1.7', fontStyle: 'italic', borderBottom: '1px solid rgba(184,211,207,0.3)', paddingBottom: '16px' }}>
                    {report.narrative.opening}
                  </p>
                )}
                <NarrativeSection title="Regulation Capacity" text={report.narrative.regulation} />
                <NarrativeSection title="Stability" text={report.narrative.stability} />
                <NarrativeSection title="Consistency" text={report.narrative.consistency} />
                <NarrativeSection title="Integration" text={report.narrative.integration} />
                {report.narrative.recommendation && (
                  <div style={{
                    marginTop: '20px', padding: '16px 18px',
                    background: 'rgba(30,107,104,0.05)', borderRadius: '12px',
                    borderLeft: '3px solid #1E6B68'
                  }}>
                    <div style={{ fontSize: '0.68rem', color: '#2D8B87', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '700', marginBottom: '6px' }}>
                      Recommendation
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#444', lineHeight: '1.65' }}>
                      {report.narrative.recommendation}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '16px 20px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#B8D3CF', fontStyle: 'italic' }}>
              This report is generated by HYPSEYA for personal insight purposes only.
              It does not constitute a clinical diagnosis or medical assessment.
            </p>
          </div>

        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body > * { display: none !important; }
          #clinical-report-print { display: block !important; }
          #clinical-report-print * { -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
